/**
 * 🧱 Refactor BT Phase 3 — T1 : `useBtEditerEffets` — code déplacé TEL QUEL depuis EditerBonTravailDialog.jsx
 * (ex-L328-694, avant T1) : 11 `useEffect` (heures budgétées auto, sync chef du dossier, types d'échafauds, remorque, alertes nacelle/lavage, clients, véhicules de livraison, disponibilité échafauds, congés/occupation équipe) + `toggleDate`. Ordre relatif des effets et tableaux de dépendances INCHANGÉS (région contiguë).
 * `ctx` = noms du composant lus par ce code (mêmes valeurs de rendu qu'avant) ; retourne ce qu'il déclare.
 */
import { API_URL } from '../components/crm/bon-travail/utils';
import axios from 'axios';
import { toast } from 'sonner';
import { useEffect, useState } from 'react';

export function useBtEditerEffets(ctx) {
  const {
    bon,
    employees,
    employesEffectifsIds,
    form,
    getEmpDates,
    lockedInfo,
    membresExclus,
    membresExtras,
    nacellesDisponibles,
    setAlerteNacelle,
    setAlerteVehicules,
    setClientsListe,
    setCongesEquipe,
    setEchafaudsDisponibilite,
    setEquipeOccupee,
    setForm,
    setLeaveConflicts,
    setLoadingClients,
    setLoadingDisponibilite,
    setLoadingVehicules,
    setNacellesDisponibles,
    setTypesEchafauds,
    setVehiculesLivraison,
    type,
  } = ctx;

  // Charger la disponibilité de l'équipe sélectionnée
  useEffect(() => {
    if (form.chef_equipe_id) {
      axios.get(`${API_URL}/api/crm/equipe-disponibilite/${form.chef_equipe_id}`)
        .then(res => res.data)
        .then(data => setEquipeOccupee(data.dates_occupees || {}))
        .catch(() => setEquipeOccupee({}));
      // Charger les congés des membres de l'équipe
      axios.get(`${API_URL}/api/conges/calendrier`).then(r => r.data)
        .then(data => {
          // 🐛 [Phase v13.46 — 2026-07-14] Utilise employesEffectifsIds au lieu de
          //   l'ancien filtre statique `e.chef_equipe_id === form.chef_equipe_id`.
          const equipeIds = Array.from(employesEffectifsIds);
          const congesMap = {};
          for (const c of (data.conges || [])) {
            if (equipeIds.includes(c.employee_id) && c.date_debut) {
              const start = new Date(c.date_debut + 'T12:00:00');
              const end = new Date((c.date_fin || c.date_debut) + 'T12:00:00');
              for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                const ds = d.toISOString().split('T')[0];
                congesMap[ds] = c.employee_nom || 'Congé';
              }
            }
          }
          setCongesEquipe(congesMap);
        })
        .catch(() => setCongesEquipe({}));
    } else {
      setEquipeOccupee({});
    }
  }, [form.chef_equipe_id]);

  // ✅ ÉTAPE 4 : Charger les types d'échafauds + la remorque source depuis l'inventaire
  const [remorqueEchafaudsId, setRemorqueEchafaudsId] = useState(null);
  useEffect(() => {
    axios.get(`${API_URL}/api/unites-rangement`)
      .then(res => res.data)
      .then(data => {
        const remorques = (data.unites || []).filter(u => u.est_remorque_echafauds);
        // Prendre la 1ère remorque qui a des types_etages configurés
        const primary = remorques.find(r => (r.types_etages || []).length > 0) || remorques[0] || null;
        setRemorqueEchafaudsId(primary?.id || null);
        const types = primary ? (primary.types_etages || []) : [];
        // Fallback vers ancien format si types_etages vide
        if (types.length === 0) {
          for (const r of remorques) {
            for (const item of (r.inventaire_echafauds || [])) {
              if (!types.find(t => t.type === item.type && t.dimensions === item.dimensions)) {
                types.push({
                  id: `${item.type}_${item.dimensions}`,
                  type: item.type,
                  dimensions: item.dimensions,
                  quantite_totale: item.quantite_totale,
                });
              }
            }
          }
        }
        setTypesEchafauds(types);
      })
      .catch(() => { setTypesEchafauds([]); setRemorqueEchafaudsId(null); });
  }, []);

  // ✅ Charger les nacelles disponibles depuis l'inventaire Élévation
  useEffect(() => {
    axios.get(`${API_URL}/api/equipements-inventaire?actif=true`)
      .then(res => res.data)
      .then(data => {
        // Filtrer uniquement les nacelles
        const nacelles = (data.equipements || []).filter(e => e.est_nacelle);
        setNacellesDisponibles(nacelles);
      })
      .catch(() => setNacellesDisponibles([]));
  }, []);

  // ✅ AUTO-SÉLECTION : Quand on coche "Nacelle requise" (Équipements de location)
  // 🆕 [5.3] Utilise l'agrégateur central pour choisir une nacelle 100% libre
  //   sur les dates sélectionnées. Si aucune date n'est encore sélectionnée,
  //   on retombe sur l'ancienne logique (première nacelle de la liste).
  //   Si une nacelle libre existe → sélection auto + toast vert.
  //   Sinon → toast d'avertissement + sélection manuelle requise.
  useEffect(() => {
    if (!form.equipements_nacelle_requise || nacellesDisponibles.length === 0 || form.equipements_nacelle_id) return;
    const datesArr = (form.equipements_nacelle_dates || []).map(d => d.date).filter(Boolean);
    if (datesArr.length === 0) {
      // Pas de dates encore → fallback ancien comportement (sélection 1ère)
      const premiereNacelle = nacellesDisponibles[0];
      setForm(f => ({ ...f, equipements_nacelle_id: premiereNacelle.id }));
      toast.success(`Nacelle "${premiereNacelle.nom}" pré-sélectionnée — choisissez les dates pour valider la dispo`);
      return;
    }
    // Avec dates : appelle l'agrégateur pour trouver une nacelle 100% libre
    axios.get(`${API_URL}/api/equipements/disponibilite`, {
      params: { type: 'nacelle', dates: datesArr.join(','), exclure_bt_id: bon?.id || '' }
    }).then(({ data }) => {
      const libre = (data.equipements || []).find(e => e['100_pct_libre_pour_toutes_dates']);
      if (libre) {
        setForm(f => ({ ...f, equipements_nacelle_id: libre.id }));
        toast.success(`Nacelle "${libre.nom}" sélectionnée (libre pour toutes les dates)`);
      } else {
        toast.warning("Aucune nacelle 100% libre pour ces dates — sélection manuelle requise");
      }
    }).catch(() => {
      // Fallback silencieux : sélection 1ère (ancien comportement)
      const premiereNacelle = nacellesDisponibles[0];
      setForm(f => ({ ...f, equipements_nacelle_id: premiereNacelle.id }));
    });
  }, [form.equipements_nacelle_requise, nacellesDisponibles, form.equipements_nacelle_dates, bon?.id]);

  // ✅ AUTO-SÉLECTION : Lavage à pression - auto-sélectionner nacelle
  useEffect(() => {
    if (form.nacelle_lavage_requise && nacellesDisponibles.length > 0 && !form.nacelle_lavage_id) {
      const premiereNacelle = nacellesDisponibles[0];
      setForm(f => ({ 
        ...f, 
        nacelle_lavage_id: premiereNacelle.id,
        nacelle_lavage_nom: premiereNacelle.nom
      }));
      toast.success(`Nacelle "${premiereNacelle.nom}" sélectionnée automatiquement pour le lavage`);
    }
  }, [form.nacelle_lavage_requise, nacellesDisponibles]);

  // ✅ NOUVEAU : Charger la liste des clients (Phase 4)
  useEffect(() => {
    setLoadingClients(true);
    axios.get(`${API_URL}/api/crm/clients`)
      .then(res => res.data)
      .then(data => setClientsListe(data.clients || []))
      .catch(() => setClientsListe([]))
      .finally(() => setLoadingClients(false));
  }, []);
  
  // ✅ PHASE 2 : Vérification disponibilité nacelle en temps réel
  useEffect(() => {
    if (!form.equipements_nacelle_id || form.equipements_nacelle_dates.length === 0) {
      setAlerteNacelle(null);
      return;
    }
    
    const dates = form.equipements_nacelle_dates.map(d => d.date);
    axios.post(`${API_URL}/api/vehicules/verifier-disponibilite-nacelle-dates`, {
      nacelle_id: form.equipements_nacelle_id,
      dates: dates,
      bon_travail_id_exclu: bon?.id
    })
      .then(res => res.data)
      .then(data => {
        if (!data.disponible) {
          setAlerteNacelle({
            type: 'warning',
            message: `⚠️ Cette nacelle est déjà réservée pour ${data.dates_conflits.length} date(s)`,
            details: data.details_conflits
          });
        } else {
          setAlerteNacelle({ type: 'success', message: '✅ Nacelle disponible pour toutes les dates' });
        }
      })
      .catch(() => setAlerteNacelle(null));
  }, [form.equipements_nacelle_id, form.equipements_nacelle_dates]);

  // ✅ PHASE 1 : Vérification disponibilité véhicules de livraison
  useEffect(() => {
    if (!form.equipements_nacelle_livraison_timing || form.equipements_nacelle_livraison_timing === 'jour_j') {
      setAlerteVehicules(null);
      return;
    }
    
    // Calculer la date de livraison (veille du premier jour)
    if (form.equipements_nacelle_dates.length === 0) return;
    
    const premierJour = form.equipements_nacelle_dates[0].date;
    const dateLivraison = new Date(premierJour + 'T00:00:00');
    dateLivraison.setDate(dateLivraison.getDate() - 1);
    const dateLivraisonStr = dateLivraison.toISOString().split('T')[0];
    
    setLoadingVehicules(true);
    axios.post(`${API_URL}/api/vehicules/verifier-disponibilite-livraison`, {
      date: dateLivraisonStr,
      type_livraison: 'nacelle',
      bon_travail_id_exclu: bon?.id
    })
      .then(res => res.data)
      .then(data => {
        setVehiculesLivraison(data.vehicules_disponibles || []);
        if (data.tous_occupes) {
          setAlerteVehicules({
            type: 'warning',
            message: `⚠️ Tous vos véhicules sont occupés la veille (${dateLivraisonStr})`,
            suggestion: data.suggestion
          });
        } else {
          setAlerteVehicules({ type: 'info', message: `${data.vehicules_disponibles.length} véhicule(s) disponible(s)` });
        }
      })
      .catch(() => setAlerteVehicules(null))
      .finally(() => setLoadingVehicules(false));
  }, [form.equipements_nacelle_livraison_timing, form.equipements_nacelle_dates]);
  
  // ✅ ÉTAPE 4 : Vérifier la disponibilité des échafauds quand on change le type/nb/dates
  useEffect(() => {
    if (!form.echafauds_a_monter || !form.type_etage_echafauds || !form.dimensions_etage_echafauds || form.dates_chantier.length === 0) {
      setEchafaudsDisponibilite(null);
      return;
    }
    
    const checkDisponibilite = async () => {
      setLoadingDisponibilite(true);
      try {
        const res = await axios.post(`${API_URL}/api/vehicules/echafauds-disponibilite`, {
          type_etage: form.type_etage_echafauds,
          dimensions: form.dimensions_etage_echafauds,
          date_debut: form.dates_chantier[0],
          date_fin: form.dates_chantier[form.dates_chantier.length - 1],
          bon_travail_id_exclu: bon?.id
        });
        setEchafaudsDisponibilite(res.data);
      } catch (e) {
        setEchafaudsDisponibilite(null);
      }
      setLoadingDisponibilite(false);
    };
    
    const timeout = setTimeout(checkDisponibilite, 500); // Debounce
    return () => clearTimeout(timeout);
  }, [form.echafauds_a_monter, form.type_etage_echafauds, form.dimensions_etage_echafauds, form.dates_chantier, bon?.id]);


  const toggleDate = (dateStr) => {
    // 🔒 2026-05-14 — Refus de retirer une date de chantier verrouillée
    //   (heures déjà enregistrées). L'ajout d'une date reste libre.
    if ((lockedInfo?.locked_dates || []).includes(dateStr)) {
      // On essaie de retirer une date verrouillée ?
      if ((form.dates_chantier || []).includes(dateStr)) {
        toast.error(
          "🔒 Date verrouillée : des heures ont déjà été enregistrées sur cette journée. Impossible de la retirer.",
          { duration: 4500 }
        );
        return;
      }
    }
    setForm(f => ({
      ...f,
      dates_chantier: f.dates_chantier.includes(dateStr)
        ? f.dates_chantier.filter(d => d !== dateStr)
        : [...f.dates_chantier, dateStr].sort(),
    }));
  };

  // Vérification des congés des employés réellement assignés (chef + team - exclus + extras)
  // 🆕 2026-05-18 — N'alerter QUE pour des congés sur des dates >= aujourd'hui.
  //   Les conflits sur des journées déjà passées ne sont plus actionnables et
  //   créent du bruit à l'enregistrement (l'utilisateur a déjà vu/oublié).
  useEffect(() => {
    if (form.dates_chantier.length === 0) {
      setLeaveConflicts([]);
      return;
    }
    const today = new Date().toISOString().slice(0, 10);
    const futureDates = [...form.dates_chantier].filter(d => d >= today).sort();
    if (futureDates.length === 0) {
      // Toutes les dates du BT sont passées → aucun conflit à signaler
      setLeaveConflicts([]);
      return;
    }
    const debut = futureDates[0];
    const fin = futureDates[futureDates.length - 1];
    axios.get(`${API_URL}/api/conges/employes-en-conge?date_debut=${debut}&date_fin=${fin}`).then(r => r.data)
      .then(conges => {
        // Liste réelle des employés assignés au moment présent (chef + équipe par défaut - exclus + extras)
        // 🆕 [Phase 26 P0] Les spécialistes lavage ne sont JAMAIS auto-injectés dans l'équipe d'un BT régulier.
        // Ils n'apparaissent donc pas comme « conflit » sans avoir été explicitement assignés.
        // 🐛 [Phase v13.46 — 2026-07-14] Utilise employesEffectifsIds (source de vérité)
        //   au lieu de l'ancien filtre statique cassé post-Phase 107.F.
        const assignesIds = new Set();
        if (form.chef_equipe_id) assignesIds.add(form.chef_equipe_id);
        for (const e of employees) {
          if (employesEffectifsIds.has(e.id) && e.est_actif && !membresExclus.has(e.id)) {
            assignesIds.add(e.id);
          }
        }
        for (const m of (membresExtras || [])) {
          if (m && m.id) assignesIds.add(m.id);
        }
        // 🆕 [v10 — 2026-06-12 Bug #2 P0 fix — fausse alerte conflit congé]
        //   Filtre supplémentaire : pour chaque congé, vérifier que l'employé
        //   est RÉELLEMENT présent au moins UN jour du congé (via la matrice
        //   `presence_par_jour` exposée par getEmpDates). Si l'utilisatrice a
        //   décoché TOUS les jours de Benjamin Savard (🚫 partout dans la
        //   matrice), il ne doit PAS apparaître comme conflit même s'il est
        //   dans l'équipe par défaut du chef.
        //
        //   Référence demande utilisatrice : « Tu mets toujours des alertes
        //   d'employés en congés même si ils ne sont pas sélectionnés sur le
        //   BT. Regarde Benjamin Savard. »
        const filtered = conges.filter(c => {
          if (!assignesIds.has(c.employee_id)) return false;
          const dFin = c.date_fin || c.date_debut;
          if (dFin && dFin < today) return false; // congé déjà terminé
          // Récupérer les jours effectivement assignés à l'employé via la matrice
          const empIsChef = c.employee_id === form.chef_equipe_id;
          const datesEmp = getEmpDates(c.employee_id, empIsChef);
          if (!datesEmp || datesEmp.length === 0) {
            // 🚫 sur tous les jours → décoché intentionnellement → ne pas alerter
            return false;
          }
          // Vérifier qu'au moins 1 jour assigné chevauche la plage du congé
          const cDebut = c.date_debut;
          const cFin = c.date_fin || c.date_debut;
          const overlap = datesEmp.some(d => d >= cDebut && d <= cFin);
          return overlap;
        });
        setLeaveConflicts(filtered);
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.chef_equipe_id, JSON.stringify(form.dates_chantier), JSON.stringify(Array.from(membresExclus)), JSON.stringify(membresExtras), JSON.stringify(form.employes_assignes), JSON.stringify(form.chef_dates_assignees)]);

  return { remorqueEchafaudsId, setRemorqueEchafaudsId, toggleDate };
}
